import { Component, inject } from '@angular/core';
import { ServiceLibraryDetailsPayload, ServiceLibraryDetailsRequest, ServiceScheduleDetailResponse } from '../../models/service-library.models';
import { NgbDropdownModule, NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { BaseServiceLibraryComponent } from '../../base-service-library.component';
import { Subject } from 'rxjs';
import { ServiceTypes } from '../../../../_shared/enums/service-type.enum';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { UpsertServiceScheduleModalComponent } from '../modals/upsert-service-schedule-modal/upsert-service-schedule-modal.component';
import { SelectServicePartsModalComponent } from '../modals/select-service-parts-modal/select-service-parts-modal.component';
import { LookupService } from '../../../../_shared/services/lookup.service';
import { LookupPartType } from '../../../../_shared/models/lookup.model';
import { ScheduleType } from '../../../../_shared/enums/service-schedule-type.enum';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-service-library-details',
  standalone: true,
  imports: [NgbDropdownModule, EmptyStateComponent, TranslocoPipe],
  templateUrl: './service-library-details.component.html',
  styleUrl: './service-library-details.component.scss'
})
export class ServiceLibraryDetailsComponent extends BaseServiceLibraryComponent {
  request: ServiceLibraryDetailsRequest;
  payload: ServiceLibraryDetailsPayload;
  serviceDetails: ServiceScheduleDetailResponse;
  errorMessage: string = '';
  hasError: boolean = false;
  customOverlayImage: string = 'assets/images/asset-management/maintenance/service-library/mercedes-benz-overlay-image.png';
  readonly ScheduleType = ScheduleType;

  private modalService: NgbModal = inject(NgbModal);
  private lookupService: LookupService = inject(LookupService);
  private readonly cancelSubject = new Subject<void>();
  private partTypes: LookupPartType[] = [];

  constructor() {
    super();
    this.payload = history.state?.payload as ServiceLibraryDetailsPayload;
    if (!this.payload) this.router.navigate(['/asset-management/service-library']);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.selectedServiceType = this.payload.serviceType;
    this.hydrateOemFiltersFromPayload();
    void this.loadDetailsAfterFiltersRestored();
  }

  private hydrateOemFiltersFromPayload(): void {
    if (this.schedule.serviceScheduleTypeId !== ScheduleType.OEM) {
      return;
    }

    this.models = [...(this.payload.models ?? [])];
    this.isLoadingModels = false;
    this.selectedModel = this.models.find(model => model.id === this.payload.modelId) ?? null;
    this.years = [...(this.payload.years ?? [])];
    this.selectedYear = this.payload.year ?? null;
    this.serviceTypeOptions = [...(this.payload.serviceTypeOptions ?? [])];
  }

  private async loadDetailsAfterFiltersRestored(): Promise<void> {
    if (this.schedule.serviceScheduleTypeId === ScheduleType.OEM) {
      if (!this.models.length) {
        await this.fetchModelOptionsAsync(this.schedule.id);
        this.selectedModel = this.models.find(model => model.id === this.payload.modelId) ?? null;
      }

      if (this.selectedModel != null) {
        if (!this.years.length) {
          await this.fetchYearOptions();
        }
      }

      if (this.payload.year != null && this.years.includes(this.payload.year)) {
        this.selectedYear = this.payload.year;
      }

      if (this.selectedModel != null && this.selectedYear != null && !this.serviceTypeOptions.length) {
        await this.fetchOemAssingedServiceTypes();
      }
    }

    await this.getServiceScheduleDetailsAsync();
  }

  async getServiceScheduleDetailsAsync(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;
    this.serviceDetails = null!;
    this.errorMessage = '';

    try {
      this.cancelSubject.next();
      this.request = {
        id: this.schedule.id,
        modelId: this.selectedModel?.id ?? this.payload?.modelId ?? null,
        year: this.selectedYear ?? this.payload?.year ?? null,
        serviceTypeId: this.selectedServiceType ?? this.payload.serviceType
      }
      const response = await this.serviceLibraryService.getServiceScheduleDetailsAsync(this.request, this.cancelSubject);
      if (response?.data) {
        this.serviceDetails = response.data;
      }
      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: false,
        callback: (message) => {
          this.hasError = true;
          this.errorMessage = message;
          this.isLoading = false;
        }
      });
    }
  }

  get serviceType(): string {
    const activeServiceType = this.selectedServiceType ?? this.payload?.serviceType;
    return activeServiceType != null
      ? this.translateServiceTypeLabel(activeServiceType)
      : '';
  }

  protected override onSelectServiceType(serviceType: ServiceTypes): void {
    if (this.serviceTypeDisabled) return;

    this.selectedServiceType = serviceType;
    this.getServiceScheduleDetailsAsync();
  }

  protected override onBackButtonClick(): void {
    this.router.navigate(['/asset-management/service-library']);
  }

  protected override shouldAutoLoadFilters(): boolean {
    return false;
  }

  protected override applyPageFilterState(pageFilter: any): void { }

  protected override onRefresh(): void { }
  protected override shouldRefresh(): boolean { return false; }
  protected override getFilters(): any { return {}; }

  openCreateMaintenancePlanModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      windowClass: 'service-schedule-modal-window'
    };

    const modalRef = this.modalService.open(UpsertServiceScheduleModalComponent, modalOptions);
    modalRef.componentInstance.scheduleId = this.schedule.id;
    modalRef.componentInstance.isEditMode = true;
    modalRef.closed.subscribe(() => {
      this.getServiceScheduleDetailsAsync();
    });
  }

  async openSelectPartsModal(): Promise<void> {
    if (!this.partTypes.length) {
      const response = await this.lookupService.getPartTypesAsync();
      if (response?.data) this.partTypes = response.data;
    }

    const selectedPartIds = this.serviceDetails?.parts?.map(p => p.id) ?? [];

    const modalRef = this.modalService.open(SelectServicePartsModalComponent, {
      centered: true,
      windowClass: 'select-service-parts-modal-window'
    });

    modalRef.componentInstance.partTypes = this.partTypes;
    modalRef.componentInstance.selectedPartIds = selectedPartIds;

    modalRef.result.then(async (selectedParts: LookupPartType[]) => {
      try {
        const partTypeIds = selectedParts.map(p => p.id);
        const serviceTypeId = this.selectedServiceType ?? this.payload.serviceType;

        await this.serviceLibraryService.updateIntervalPartsAsync(this.schedule.id, serviceTypeId, partTypeIds);
        this.getServiceScheduleDetailsAsync();
      } catch (error) {
        this.errorHandlerService.handleError(error);
      }
    }).catch(() => { });
  }
}